// The konsultan app (POC). A konsultan account (created by the admin in
// Akun) signs in at /portal and lands here:
//   /pendampingan       the requests assigned to me, newest first: topic,
//                       paket, Penyedia, stage, status
//   /pendampingan/:id   one request: the paket read-only (header, stepper,
//                       checklist of every stage, the project's file names)
//                       beside the request and its notes thread, where the
//                       konsultan writes notes and marks the request Selesai.
// Nothing here changes the Penyedia's project; the API answers 404 for a
// request assigned to someone else and never opens the owner's other projects.
import { useId, useState, type FormEvent, type ReactNode } from "react";
import * as API from "../api";
import type { Consultation, ConsultantView } from "../api";
import { formatDate, formatRelative } from "../lib/format";
import { fileGlyph } from "../project/files";
import { ChecklistReadOnly, ConsultationNotes, ConsultationStatusLine, PaketHeader, StageStepper } from "../project/PaketParts";
import { STAGE_LABEL } from "../project/stages";
import { EmptyState, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export function requestPath(id: string): string {
  return `/pendampingan/${encodeURIComponent(id)}`;
}

function LoadError({ error, reload, title }: { error: unknown; reload: () => void; title: string }) {
  return (
    <Notice
      tone="danger"
      title={title}
      action={
        <button type="button" className="btn btn-secondary" onClick={reload}>
          Coba lagi
        </button>
      }
    >
      <p>{API.errorMessage(error)}</p>
    </Notice>
  );
}

export function AssignedListScreen() {
  useTitle("Pendampingan");
  const list = useAsync<Consultation[]>(() => API.listAssigned(), []);
  let body: ReactNode;
  if (list.loading && !list.data) body = <SkeletonRows count={3} label="Memuat permintaan" />;
  else if (list.error && !list.data) body = <LoadError error={list.error} reload={list.reload} title="Permintaan belum termuat" />;
  else if ((list.data ?? []).length === 0) {
    body = <EmptyState icon="messages" title="Belum ada permintaan" body="Permintaan pendampingan yang ditugaskan admin kepada Anda tampil di sini." />;
  } else {
    const open = (list.data ?? []).filter((c) => c.status !== "selesai");
    const done = (list.data ?? []).filter((c) => c.status === "selesai");
    body = (
      <>
        <RequestRows title="Perlu ditangani" rows={open} empty="Semua permintaan sudah selesai." />
        {done.length > 0 ? <RequestRows title="Selesai" rows={done} /> : null}
      </>
    );
  }
  return (
    <div className="pd-page">
      <PageHeader title="Pendampingan" lead="Permintaan pendampingan dari Penyedia yang ditugaskan kepada Anda." />
      {body}
    </div>
  );
}

function RequestRows({ title, rows, empty }: { title: string; rows: Consultation[]; empty?: string }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="pd-page">
      <h2 id={id} className="pj-panel-title">
        {title} <span className="pd-meta num">({rows.length})</span>
      </h2>
      {rows.length === 0 ? (
        <p className="pd-empty">{empty}</p>
      ) : (
        <ul className="pd-rows">
          {rows.map((c) => (
            <li key={c.id}>
              <a className="pd-row" href={requestPath(c.id)}>
                <span className="pd-row-top">
                  <span className="pd-row-title">{c.topic}</span>
                  <ConsultationStatusLine consultation={c} />
                </span>
                <span className="pd-meta">
                  {c.projectName}, {c.requesterName}, tahap {STAGE_LABEL[c.stage]}
                </span>
                <span className="pd-meta num">
                  Diminta {formatDate(c.createdAt)}, diperbarui {formatRelative(c.updatedAt)}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AssignedDetailScreen({ id }: { id: string }) {
  const view = useAsync<ConsultantView>(() => API.getAssigned(id), [id]);
  useTitle(view.data ? `Pendampingan: ${view.data.consultation.topic}` : "Pendampingan");
  const back = { href: "/pendampingan", label: "Kembali ke Pendampingan" };

  if (view.loading && !view.data) {
    return (
      <div className="pd-page">
        <SkeletonRows count={4} label="Memuat permintaan" />
      </div>
    );
  }
  if (view.error && !view.data) {
    const gone = view.error instanceof API.ApiError && view.error.status === 404;
    return (
      <div className="pd-page">
        {gone ? (
          <EmptyState
            icon="messageQuestion"
            title="Permintaan tidak ditemukan"
            body="Permintaan ini tidak ditugaskan kepada Anda."
            action={
              <a className="btn btn-secondary" href={back.href}>
                {back.label}
              </a>
            }
          />
        ) : (
          <LoadError error={view.error} reload={view.reload} title="Permintaan belum termuat" />
        )}
      </div>
    );
  }
  const data = view.data!;
  const { project, consultation } = data;
  const stage = project.stage ?? "persiapan_pemilihan";
  return (
    <div className="pd-page">
      <a className="back-link" href={back.href}>
        <Glyph name="arrowLeft" />
        <span>{back.label}</span>
      </a>
      <div className="pd-view">
        <div className="pd-col">
          <PaketHeader project={project} />
          <p className="pd-readonly">
            <Glyph name="eye" size={16} />
            <span>Hanya baca. Perubahan paket dilakukan oleh Penyedia.</span>
          </p>
          <section className="pd-card" aria-labelledby="pd-stage-title">
            <h2 id="pd-stage-title" className="pd-card-title">
              Tahap paket
            </h2>
            <StageStepper stage={stage} />
            {project.closingNote ? <p className="pd-message">Catatan penutupan: {project.closingNote}</p> : null}
            <ChecklistReadOnly groups={data.checklist} stage={stage} />
          </section>
          <section className="pd-card" aria-labelledby="pd-files-title">
            <h2 id="pd-files-title" className="pd-card-title">
              File project
            </h2>
            {data.files.length === 0 ? (
              <p className="pd-empty">Belum ada file di project ini.</p>
            ) : (
              <ul className="pd-files">
                {data.files.map((f) => (
                  <li key={f.driveFileId} className="pd-file">
                    <Glyph name={fileGlyph(f.mimeType, f.name)} size={20} />
                    <span title={f.name}>{f.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <div className="pd-col">
          <RequestThread consultation={consultation} onChange={(c) => view.set({ ...data, consultation: c })} />
        </div>
      </div>
    </div>
  );
}

function RequestThread({ consultation: c, onChange }: { consultation: Consultation; onChange: (c: Consultation) => void }) {
  const id = useId();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<"note" | "status" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy("note");
    setError(null);
    try {
      onChange(await API.addConsultantNote(c.id, text.trim()));
      setText("");
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const toggle = async () => {
    setBusy("status");
    setError(null);
    try {
      onChange(await API.setConsultantStatus(c.id, c.status === "selesai" ? "ditugaskan" : "selesai"));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="pd-card" aria-labelledby={`${id}-title`}>
      <div className="pd-item-head">
        <h2 id={`${id}-title`} className="pd-card-title">
          {c.topic}
        </h2>
        <span className="pd-stage">Tahap {STAGE_LABEL[c.stage]}</span>
      </div>
      <ConsultationStatusLine consultation={c} />
      <p className="pd-meta">
        Dari {c.requesterName} untuk project {c.projectName}, {formatDate(c.createdAt)}
      </p>
      <p className="pd-message">{c.message}</p>
      <h3 className="pk-closing-title">Catatan</h3>
      <ConsultationNotes consultation={c} viewer="konsultan" />
      <form className="pd-compose" onSubmit={send}>
        <label className="visually-hidden" htmlFor={`${id}-note`}>
          Tulis catatan untuk Penyedia
        </label>
        <textarea
          id={`${id}-note`}
          rows={4}
          maxLength={4000}
          placeholder="Tulis saran, koreksi dokumen, atau langkah berikutnya untuk Penyedia"
          value={text}
          readOnly={busy !== null}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        <div className="pd-compose-actions">
          <button type="button" className="btn btn-secondary" onClick={toggle} disabled={busy !== null} aria-busy={busy === "status" || undefined}>
            {c.status === "selesai" ? "Buka kembali" : "Tandai selesai"}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy !== null || !text.trim()} aria-busy={busy === "note" || undefined}>
            Kirim catatan
          </button>
        </div>
      </form>
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </section>
  );
}
